import { selectJBPSettings } from 'legacy/lut/features/partner360/jbpSettings'
import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useMemo } from 'react'
import { useSelector } from 'react-redux'

import { SegmentedControl } from 'cng-common/components/segmentedControl'
import { Translation } from 'cng-common/features/translations'

import {
  formatCockpitScore,
  getCockpitInitiativesInScope,
  getCockpitMdfActivitiesInScope,
} from '../../../../lib'
import { setCockpitInitiativesView } from '../../../../model'
import { useCockpitKeyInitiativesData } from '../../../../model/hooks'
import {
  selectCockpitCategory,
  selectCockpitInitiativesView,
  selectCockpitOffering,
  selectCockpitPeriods,
  selectCockpitPlanningPeriod,
  selectEffectiveCockpitFYear,
} from '../../../../model/selectors'
import type { TCockpitInitiativesView } from '../../../../model/types'
import { CockpitSegmentedControlAccent } from '../../../components/cockpitSegmentedControlAccent'
import { PlanQualityBadge } from '../../../components/planQualityBadge'
import { useInitiativesQualityScore } from '../../hooks/useInitiativesQualityScore'

import { INITIATIVES_TABS } from './initiativesTabs'

import styles from './CockpitInitiativesViewSwitch.module.scss'

interface ICockpitInitiativesViewSwitchProps {
  /** `data-tour-id` for the switch, from the cockpit whose tour points at it. */
  tourId?: string
  isManagerCockpitMode?: boolean
}

/** The expanded Initiatives tile's view switch, drawn in the tile header's control row. The view
 *  is kept in the slice, so the tile body and a widget tour read the same one. */
export const CockpitInitiativesViewSwitch = ({
  tourId,
  isManagerCockpitMode = false,
}: ICockpitInitiativesViewSwitchProps) => {
  const dispatch = useAppDispatch()
  const activeTab = useSelector(selectCockpitInitiativesView)
  const qualityScore = useInitiativesQualityScore()
  const { initiatives, mdfActivities } = useCockpitKeyInitiativesData({ isManagerCockpitMode })
  const selectedOffering = useSelector(selectCockpitOffering)
  const selectedCategory = useSelector(selectCockpitCategory)
  const selectedPeriods = useSelector(selectCockpitPeriods)
  const planningPeriod = useSelector(selectCockpitPlanningPeriod)
  const fYear = useSelector(selectEffectiveCockpitFYear)
  const { kiBusinessPriorities } = useSelector(selectJBPSettings)

  const initiativesCount = useMemo(
    () =>
      getCockpitInitiativesInScope({
        initiatives,
        fYear,
        selectedOffering,
        selectedCategory,
        selectedPeriods,
        planningPeriod,
      }).length,
    [initiatives, fYear, selectedOffering, selectedCategory, selectedPeriods, planningPeriod]
  )

  const mdfCount = useMemo(
    () =>
      getCockpitMdfActivitiesInScope({
        activities: mdfActivities,
        initiatives,
        kiBusinessPriorities,
        selectedOffering,
        selectedPeriods,
        planningPeriod,
      }).length,
    [
      mdfActivities,
      initiatives,
      kiBusinessPriorities,
      selectedOffering,
      selectedPeriods,
      planningPeriod,
    ]
  )

  const handleSelectTab = (tab: TCockpitInitiativesView) => {
    dispatch(setCockpitInitiativesView(tab))
  }

  return (
    <div data-tour-id={tourId}>
      <CockpitSegmentedControlAccent inHeader>
        <SegmentedControl
          useItemKeys
          selectedItem={activeTab}
          onItemSelect={handleSelectTab}
          size="small"
          variant="veneer"
        >
          {INITIATIVES_TABS.map((tab) => (
            <SegmentedControl.Item key={tab.key} itemKey={tab.key}>
              <span className={styles.tabLabel}>
                <Translation text={tab.translationKey} span />
                {tab.key === 'quality' ? (
                  qualityScore !== undefined ? (
                    <PlanQualityBadge
                      score={qualityScore}
                      displayValue={formatCockpitScore(qualityScore)}
                      pill
                    />
                  ) : null
                ) : (
                  <span className={styles.countBadge}>
                    {tab.key === 'initiatives' ? initiativesCount : mdfCount}
                  </span>
                )}
              </span>
            </SegmentedControl.Item>
          ))}
        </SegmentedControl>
      </CockpitSegmentedControlAccent>
    </div>
  )
}

CockpitInitiativesViewSwitch.displayName = 'CockpitInitiativesViewSwitch'
